import { EntityError, OrderItemDto, ReplenishmentErrorCodes } from "@deliverr/replenishment-client";
import { SelectedProductData } from "common/components/ProductChooser";
import { ProductCollection } from "common/models";
import { buildCasePackFromProduct } from "transfers/utils/productUtils";

/**
 * Retrieves product details for a given DSKU and packOf from the product collection.
 * @param dsku - The DSKU of the product.
 * @param packOf - The packOf value associated with the product.
 * @param productCollection - The collection of products.
 * @returns Product details including case pack information.
 */
const getProductDetails = (dsku: string, packOf: string, productCollection: ProductCollection) => {
  const product = productCollection[packOf];

  if (!product) {
    throw new EntityError({
      code: ReplenishmentErrorCodes.PRODUCT_NOT_FOUND,
      entityId: packOf,
      message: `Product not found for dsku ${packOf}.`,
    });
  }

  const casePacks = [...(product.productCasePacks ?? []), buildCasePackFromProduct(product)];
  const packDetails = casePacks.find((casePack) => casePack.dsku === dsku);

  return {
    ...product,
    dsku,
    packOf,
    length: packDetails?.length,
    width: packDetails?.width,
    height: packDetails?.height,
    weight: packDetails?.weight,
    lengthUnit: packDetails?.lengthUnit,
    weightUnit: packDetails?.weightUnit,
    packName: packDetails?.packName,
  };
};

/**
 * Maps order items to selected products, combining repeated DSKUs by summing up totalUnits.
 * @param orderItems - Array of order items to map.
 * @param productCollection - Collection of products to retrieve details from.
 * @returns A mapping of DSKU to SelectedProductData.
 */
export const mapOrderItemsToSelectedProducts = (
  orderItems: OrderItemDto[],
  productCollection: ProductCollection
): { [dsku: string]: SelectedProductData } => {
  const duplicateProducts: EntityError[] = [];

  const selectedProducts = orderItems.reduce((acc, { dsku, packOf, totalUnits, unitsPerPack, lineItemId }) => {
    if (acc[dsku]) {
      duplicateProducts.push(
        new EntityError({
          code: ReplenishmentErrorCodes.DUPLICATE_ITEMS_IN_ORDER,
          entityId: lineItemId,
          message: `Duplicate DSKU: ${dsku} found in order items.`,
          payload: {
            row: {
              firstOccurrence: acc[dsku].lineItemId,
              currentOccurrence: lineItemId,
              sku: packOf,
            },
          },
        })
      );
    } else {
      const productDetails = getProductDetails(dsku, packOf, productCollection);

      acc[dsku] = {
        ...productDetails,
        qty: totalUnits,
        objectID: dsku,
        caseQty: unitsPerPack,
        numberOfCases: Math.floor(totalUnits / unitsPerPack),
        lineItemId,
        onHandQty: 0,
        nonPickableQty: 0,
        unfilledOrderQty: 0,
        inTransferQty: 0,
        unavailableQty: 0,
        unitsSoldLast30Days: 0, // TODO - Fix this
        storageOnHandQty: 0,
        storageNonPickableQty: 0,
        storageUnfilledOrderQty: 0,
        storageInTransferQty: 0,
        storageUnavailableQty: 0,
      };
    }

    return acc;
  }, {});

  if (duplicateProducts.length > 0) {
    // eslint-disable-next-line
    throw duplicateProducts;
  }

  return selectedProducts;
};
